import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LogOut } from "lucide-react";
import ReservationsView from "@/components/admin/ReservationsView";
import ContentEditor from "@/components/admin/ContentEditor";
import PagesEditor from "@/components/admin/PagesEditor";
import EmailSettingsEditor from "@/components/admin/EmailSettingsEditor";


const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("sm_admin_token", data.access_token);
      onLogin(data.access_token);
      toast.success("Mirëseardhe.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kredencialet janë të pasakta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center px-6">
      <form
        data-testid="admin-login-form"
        onSubmit={submit}
        className="w-full max-w-md border border-sm-border bg-sm-surface p-10"
      >
        <p className="text-[0.7rem] tracking-[0.32em] uppercase text-sm-olive">
          San Martino 26
        </p>
        <h1 className="font-serif text-3xl mt-4 text-sm-text">Panel Administrimi</h1>
        <p className="text-sm text-sm-muted mt-2 font-light">
          Hyni për të menaxhuar rezervimet dhe përmbajtjen.
        </p>
        <div className="mt-8 space-y-5">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="sm-admin-input"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Fjalëkalimi"
            className="sm-admin-input"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-sm-text text-sm-bg py-3 text-[0.75rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors disabled:opacity-60"
          >
            {busy ? "Duke hyrë..." : "Hyr"}
          </button>
        </div>
        <Link
          to="/"
          data-testid="admin-back-home"
          className="block mt-6 text-center text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive transition-colors"
        >
          ← Kthehu tek faqja
        </Link>
      </form>
    </div>
  );
};

const Shell = ({ token, onLogout }) => {
  const [tab, setTab] = useState("reservations");

  return (
    <div className="min-h-screen bg-transparent" data-testid="admin-dashboard">
      <header className="border-b border-sm-border bg-sm-bg/95 backdrop-blur sticky top-0 z-30">
        <div className="sm-container flex flex-wrap items-center justify-between gap-3 h-auto py-4 md:h-16 md:py-0">
          <Link to="/" className="font-serif text-lg text-sm-text">
            San Martino <span className="text-sm-terra">26</span>
            <span className="text-[0.65rem] tracking-[0.32em] uppercase text-sm-olive ml-3">
              Admin
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {[
              { id: "reservations", label: "Rezervime" },
              { id: "content", label: "Përmbajtja" },
              { id: "pages", label: "Faqet" },
              { id: "email", label: "Email" },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase border transition-colors ${tab === t.id
                  ? "border-sm-text bg-sm-text text-sm-bg"
                  : "border-transparent text-sm-muted hover:text-sm-olive"
                  }`}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-terra transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Dil
          </button>
        </div>
      </header>

      <div className="sm-container py-12">
        {tab === "reservations" && (
          <ReservationsView token={token} onUnauthorized={onLogout} />
        )}
        {tab === "content" && <ContentEditor token={token} />}
        {tab === "pages" && (
          <PagesEditor token={token} onUnauthorized={onLogout} />
        )}
        {tab === "email" && <EmailSettingsEditor token={token} />}
      </div>

      <style>{`
        .sm-admin-input {
          width: 100%;
          background: hsl(var(--sm-bg));
          border: 1px solid hsl(var(--sm-border));
          padding: 12px 14px;
          font-size: 14px;
          color: hsl(var(--sm-text));
          border-radius: 2px;
          outline: none;
          font-family: "Inter", sans-serif;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .sm-admin-input:focus { border-color: hsl(var(--sm-olive)); box-shadow: 0 0 0 2px hsl(var(--sm-olive) / 0.12); }
        .sm-admin-input::placeholder { color: hsl(var(--sm-text-muted) / 0.6); }
      `}</style>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("sm_admin_token"));

  useEffect(() => {
    document.title = "Admin · San Martino 26";
  }, []);

  const logout = () => {
    localStorage.removeItem("sm_admin_token");
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Shell token={token} onLogout={logout} />;
};

export default Admin;
